import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

/**
 * Shared layout for compliance / policy pages. Gives every legal page the
 * same header, left-aligned editorial typography, breadcrumb, table of
 * contents, and footer.
 *
 * Legal pages stay in English (legally binding). When the UI language is
 * NOT English, a small disclaimer banner at the top of the article explains
 * that the body remains English for legal accuracy.
 */
export default function PolicyLayout({ title, eyebrow, lastUpdated, toc, children, testid }) {
  const { t, i18n } = useTranslation();
  const lng = (i18n.resolvedLanguage || "en").slice(0, 2);
  const showDisclaimer = lng !== "en";
  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid={testid}>
      <Header />

      <section className="border-b hairline bg-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <nav className="text-xs mono text-[#888] mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#ff4500]">Home</Link>
            <span className="mx-2">/</span>
            <span>{eyebrow || "Legal"}</span>
          </nav>
          <div className="eyebrow mb-3">{eyebrow}</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-4">{title}</h1>
          {lastUpdated && (
            <div className="text-xs mono text-[#555]">Last updated · {lastUpdated}</div>
          )}
        </div>
      </section>

      <section className="flex-1 bg-[#fafafa]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-[180px_1fr] gap-10">
          {toc && toc.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="eyebrow mb-3">Contents</div>
                <ul className="space-y-2 text-sm">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="text-[#333] hover:text-[#ff4500]">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
          <article className="prose-policy max-w-none">
            {showDisclaimer && (
              <div
                className="flex items-start gap-3 p-4 border hairline bg-[#fff4ee] mb-8 text-sm text-[#7a2400] leading-snug"
                data-testid="policy-translation-disclaimer"
                role="note"
              >
                <Info className="w-4 h-4 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={2} />
                <span>{t("policy.machineTranslatedNotice")}</span>
              </div>
            )}
            {children}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Small, consistent helpers exposed for the policy pages
export const PolicyH2 = ({ id, children }) => (
  <h2
    id={id}
    className="font-display text-2xl tracking-tight mt-12 mb-4 scroll-mt-24 text-[#111]"
  >
    {children}
  </h2>
);

export const PolicyH3 = ({ children }) => (
  <h3 className="font-display text-lg tracking-tight mt-8 mb-3 text-[#222]">{children}</h3>
);

export const PolicyP = ({ children }) => (
  <p className="text-[15px] leading-relaxed text-[#333] mb-4">{children}</p>
);

export const PolicyUL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 text-[15px] leading-relaxed text-[#333] mb-4">
    {children}
  </ul>
);

export const PolicyCallout = ({ children, variant = "info" }) => (
  <div
    className={`p-4 border hairline my-6 text-sm leading-relaxed ${
      variant === "warn" ? "bg-[#fff4ee] text-[#7a2400]" : "bg-[#fafafa] text-[#333]"
    }`}
  >
    {children}
  </div>
);
